import * as React from "react";
import { useDebouncedCallback } from "use-debounce";

import { searchIndustriesQuery } from "@brexhq/next/src/api/marketo";

import { SearchIndustriesQuery } from "../gqlTypes/SearchIndustriesQuery";
import { connectionToNodes } from "../helpers/graphql";

export interface SearchResult<V> {
  label: string;
  value: V;
}

const SEARCH_DEBOUNCE = 200;
const LIMIT = 1000;

export const useIndustrySearchResultsQuery = () => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [data, setData] = React.useState<SearchIndustriesQuery | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const debouncedSearch = useDebouncedCallback(setSearchTerm, SEARCH_DEBOUNCE);

  React.useEffect(() => {
    if (!searchTerm) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchIndustries = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await searchIndustriesQuery(searchTerm, LIMIT);

        if (response.errors) {
          throw new Error(
            response.errors.map((e: any) => e.message).join(", "),
          );
        }

        setData(response.data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Unknown error"));
      } finally {
        setLoading(false);
      }
    };

    fetchIndustries();
  }, [searchTerm]);

  const industrySearchResults: SearchResult<string>[] = React.useMemo(() => {
    return connectionToNodes(data?.searchIndustries).map(({ industry }) => ({
      label: industry.displayName,
      value: industry.internalName,
    }));
  }, [data?.searchIndustries]);

  const clearSearch = React.useCallback(() => {
    setSearchTerm("");
    debouncedSearch("");
  }, [setSearchTerm, debouncedSearch]);

  return {
    industrySearchResults,
    debouncedSearch,
    clearSearch,
    loading,
    error,
  };
};
